import {
  DocumentServicesObject,
  CompRef,
  InnerElementsHierarchyStructure,
  InnerElementsPathsBuilderStructures,
} from '@wix/document-services-types';
import type { EditorPointer, EditorPointerAPI } from '@wix/editorx-core-api';
import type {
  ResolvedManifest,
  ResolvedEditorElement,
} from '@wix/component-protocol/resolver';
import { Action } from '@wix/component-protocol/schema';
import type { ResolvedInnerElement } from '@wix/editor-platform-manifest-services';
import { createElement } from 'react';
import { PanelsStore } from '../../services/builderServices/panelsStore';
import {
  createEditorPlatformBuilderServicesError,
  EditorPlatformBuilderServicesErrorCode,
} from '../../services/builderServices/errors';
import { getWorkerManager } from '../../bootstrap/init';

function createInitialInnerElementsPathsBuilderStructures(
  documentServicesAPI: DocumentServicesObject,
  manifestStore: Record<string, ResolvedManifest>,
  initialCompRefs?: CompRef[],
): {
  compPointer: CompRef;
  innerElements: ResolvedEditorElement['elements'];
}[] {
  return Object.entries(manifestStore).flatMap(([compType, manifest]) => {
    const compRefs =
      initialCompRefs ?? documentServicesAPI.components.get.byType(compType);

    if (!compRefs.length) {
      return [];
    }

    return compRefs.map((compRef) => {
      const innerElements = manifest?.editorElement?.elements;

      return {
        compPointer: compRef,
        innerElements,
      };
    });
  });
}

function buildHierarchy(
  innerElements: ResolvedEditorElement['elements'],
): InnerElementsHierarchyStructure[] {
  return Object.entries(innerElements || {}).flatMap(([key, element]) => {
    if (element === undefined) {
      return [];
    }

    if (element.elements) {
      return {
        key,
        selector: element?.selector,
        innerElements: buildHierarchy(element.elements),
      };
    }

    return {
      key,
      selector: element?.selector,
      innerElements: [],
    };
  }) as InnerElementsHierarchyStructure[];
}

function getEditorPointersTreeAsArray(
  editorPointerAPI: EditorPointerAPI,
  editorPointers: EditorPointer<CompRef>[],
  getManifest: (
    editorPointer: EditorPointer<CompRef>,
  ) => ResolvedEditorElement | ResolvedInnerElement | null,
): EditorPointer<CompRef>[] {
  return editorPointers.flatMap((editorPointer) => {
    const manifest = getManifest(editorPointer);
    return [
      editorPointer,
      ...getChildren(editorPointerAPI, editorPointer, manifest, true),
    ];
  });
}

function getChildren(
  editorPointerAPI: EditorPointerAPI,
  editorPointer: EditorPointer<CompRef>,
  componentManifest: ResolvedEditorElement | ResolvedInnerElement | null,
  isRecursive: boolean,
): EditorPointer<CompRef>[] {
  if (!componentManifest || !componentManifest.elements) {
    return [];
  }

  const innerElements = componentManifest.elements;
  return Object.entries(innerElements || {}).flatMap(
    ([key, element]: [string, ResolvedEditorElement | undefined]) => {
      if (element === undefined) {
        return [];
      }

      const documentPointer =
        editorPointerAPI.getDocumentPointer(editorPointer);
      const innerElementPath =
        editorPointerAPI.getInnerElementPath(editorPointer);

      const childPointer = editorPointerAPI.buildEditorPointer(
        documentPointer,
        [...(innerElementPath || []), key],
      );

      if (element.elements && isRecursive) {
        return [
          childPointer,
          ...getChildren(editorPointerAPI, childPointer, element, true),
        ];
      } else {
        return [childPointer];
      }
    },
  );
}

function getAncestors(
  editorPointerAPI: EditorPointerAPI,
  editorPointer: EditorPointer<CompRef>,
): EditorPointer<CompRef>[] {
  const documentPointer = editorPointerAPI.getDocumentPointer(editorPointer);
  const innerElementPath = editorPointerAPI.getInnerElementPath(editorPointer);

  if (!innerElementPath || innerElementPath.length === 1) {
    return [];
  }

  const initialPointer = editorPointerAPI.buildEditorPointer(
    documentPointer,
    innerElementPath.slice(0, 1),
  );

  const initialPointerInnerElementPath =
    editorPointerAPI.getInnerElementPath(initialPointer);

  const path = innerElementPath.slice(1, innerElementPath.length - 1);

  return path.reduce(
    (acc: EditorPointer<CompRef>[], key: string) => {
      const lastPointer = acc[acc.length - 1];
      const nextDocumentPointer =
        editorPointerAPI.getDocumentPointer(lastPointer);
      const lastPointerInnerElementPath =
        editorPointerAPI.getInnerElementPath(lastPointer);

      const nextPointerInnerElementPath = [
        ...(lastPointerInnerElementPath ||
          initialPointerInnerElementPath ||
          []),
        key,
      ];

      const nextPointer = editorPointerAPI.buildEditorPointer(
        nextDocumentPointer,
        nextPointerInnerElementPath,
      );

      return [...acc, nextPointer];
    },
    [initialPointer],
  );
}

function createInnerElementsPathsBuilderStructures(
  documentServicesAPI: DocumentServicesObject,
  manifestStore: Record<string, ResolvedManifest>,
  initialCompRefs?: CompRef[],
): InnerElementsPathsBuilderStructures {
  const initialStructure = createInitialInnerElementsPathsBuilderStructures(
    documentServicesAPI,
    manifestStore,
    initialCompRefs,
  );

  return initialStructure.map(({ compPointer, innerElements }) => {
    const hierarchy = buildHierarchy(innerElements);
    return {
      compPointer,
      innerElements: hierarchy,
    };
  });
}

function createBuildPathsFn(
  documentServicesAPI: DocumentServicesObject,
  getManifestsByCompTypes: (
    compTypes: string[],
  ) => Record<string, ResolvedManifest>,
) {
  return function buildPaths(
    loadedCompTypes: string[],
    initialComponent?: {
      compRef: CompRef;
      compType: string;
    },
  ) {
    if (
      initialComponent &&
      !loadedCompTypes.includes(initialComponent.compType)
    ) {
      loadedCompTypes.push(initialComponent.compType);
    }

    const initialCompRefs = initialComponent
      ? [initialComponent.compRef]
      : undefined;

    const resolvedManifests = getManifestsByCompTypes(loadedCompTypes);

    loadedCompTypes.length > 0 &&
      documentServicesAPI.components.innerElements.buildPaths(
        createInnerElementsPathsBuilderStructures(
          documentServicesAPI,
          resolvedManifests,
          initialCompRefs,
        ),
      );

    return resolvedManifests;
  };
}

function createPanelsLoader(
  panelsStore: PanelsStore,
  getManifest: (
    editorPointer: EditorPointer<CompRef>,
  ) => ResolvedEditorElement | ResolvedInnerElement | null,
) {
  return async function panelsLoader(editorPointers: EditorPointer<CompRef>[]) {
    const worker = await getWorkerManager();
    const host = worker.getWixClientHost();

    editorPointers.forEach((editorPointer) => {
      const manifest = getManifest(editorPointer);
      const actions = manifest?.actions ?? {};
      Object.entries(actions).forEach(
        async ([actionName, action]: [string, Action]) => {
          const panelURL = action?.execution?.panel?.inline?.code?.bundleUrl;
          if (!panelURL) {
            return;
          }

          try {
            const componentPanel = await panelsStore.loadPanel(panelURL);
            const panelWithPlatformHost = createElement(
              componentPanel,
              {
                host: () => host,
                ...componentPanel.defaultProps,
              },
              [],
            );
            panelsStore.setPanel(
              editorPointer,
              actionName,
              panelWithPlatformHost,
            );
          } catch (err) {
            throw createEditorPlatformBuilderServicesError(
              EditorPlatformBuilderServicesErrorCode.LoadComponentPanelError,
              `Failed to load component panel for action ${actionName} from the URL - [${panelURL}]`,
            ).withParentError(err as Error);
          }
        },
      );
    });
  };
}

export {
  createInnerElementsPathsBuilderStructures,
  getChildren,
  getEditorPointersTreeAsArray,
  getAncestors,
  createBuildPathsFn,
  createPanelsLoader,
};
